export type PolicyChatRole = 'user' | 'assistant';

/** What kind of reply an assistant message is, as the Policy Agent reports it. */
export type PolicyChatIntent =
  | 'short_answer'
  | 'report'
  | 'bills'
  | 'searches'
  | 'question'
  | 'denial';

export interface PolicyChatBillVersion {
  billId: string;
  /** Empty when the reference names the whole bill. */
  billVersionId: string;
  number: string | null;
  state: string | null;
  title: string | null;
  versionName: string | null;
  /**
   * In-app path to the bill version, e.g. `/legislative-tracking/bill/details/:billId/:billVersionId`,
   * or to the bill when only the bill resolved.
   */
  link: string | null;
}

export interface PolicyChatCitation {
  id: number;
  citedText: string;
  bill: PolicyChatBillVersion;
}

export interface PolicyChatMessage {
  id: number;
  role: PolicyChatRole;
  /** User text, or assistant markdown with `[n]` citation markers. */
  content: string;
  /** Assistant messages only. */
  citations: PolicyChatCitation[] | null;
  /** Assistant messages only: the bills relevant to the user's request at that turn. */
  documentRefs: PolicyChatBillVersion[] | null;
  /** Assistant messages only; null on messages stored before intents were kept. */
  intents: PolicyChatIntent[] | null;
  isRefusal: boolean;
  createdAt: string;
}

export interface PolicyChatConversationSummary {
  id: string;
  title: string;
  lastMessageAt: string;
  /** True once the conversation can no longer be continued (29 days after the last message). */
  readOnly: boolean;
}

export interface PolicyChatConversation extends PolicyChatConversationSummary {
  messages: PolicyChatMessage[];
}

export interface StartPolicyChatResponse {
  conversation: PolicyChatConversationSummary;
  /** The user message and the assistant answer, in order. */
  messages: PolicyChatMessage[];
}

export interface SendPolicyChatMessageResponse {
  conversation: PolicyChatConversationSummary;
  /** The user message and the assistant answer, in order. */
  messages: PolicyChatMessage[];
}

/** `errorType` values the policy chat routes return in an error body. */
export enum PolicyChatErrorType {
  ConversationBusy = 'conversation_busy',
  ConversationReadOnly = 'conversation_read_only',
  AgentUnavailable = 'agent_unavailable',
}

export const POLICY_CHAT_MESSAGE_MAX_LENGTH = 4000;
